import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../i18n/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Plus, Save, Eye, Store, ChevronRight, ChevronDown, FolderOpen, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import BlockEditor from '../components/BlockEditor';
import RichTextEditor from '../components/RichTextEditor';

function StoreGroupsDisplay({ groups, stores, targetGroups, onTargetGroupsChange }) {
  const [expanded, setExpanded] = React.useState({});
  const [targetExpanded, setTargetExpanded] = React.useState({});
  const storeMap = React.useMemo(() => {
    const map = {};
    stores.forEach(s => { map[s.id] = s.name; });
    return map;
  }, [stores]);

  const toggle = (groupId) => setExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  const toggleTarget = (groupId) => setTargetExpanded(prev => ({ ...prev, [groupId]: !prev[groupId] }));

  const handleCheck = (groupId, checked) => {
    if (checked) {
      onTargetGroupsChange([...targetGroups, groupId]);
    } else {
      onTargetGroupsChange(targetGroups.filter(id => id !== groupId));
    }
  };

  const hasTargets = targetGroups.length > 0;

  const targetedSummary = React.useMemo(() => {
    if (!hasTargets) return null;
    const selected = groups.filter(g => targetGroups.includes(g.id));
    const uniqueIds = new Set();
    selected.forEach(g => (g.store_ids || []).forEach(id => uniqueIds.add(id)));
    return { selected, storeCount: uniqueIds.size };
  }, [groups, targetGroups, hasTargets]);

  return (
    <div className="bg-gray-50 rounded-lg p-4 mt-3">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
        <FolderOpen className="h-4 w-4" />
        Target Store Groups
      </div>
      <p className="text-xs text-gray-500 mb-2">Select groups to target. Leave all unchecked to show all stores.</p>
      <div className="space-y-1">
        {groups.map(group => {
          const isOpen = expanded[group.id];
          const storeIds = group.store_ids || [];
          const isChecked = targetGroups.includes(group.id);
          return (
            <div key={group.id}>
              <div className="flex items-center gap-1 py-1 pl-1">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleCheck(group.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  data-testid={`target-group-check-${group.id}`}
                />
                <button
                  type="button"
                  onClick={() => toggle(group.id)}
                  className="flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900"
                  data-testid={`store-group-toggle-${group.id}`}
                >
                  {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  <span className="font-medium">{group.name}</span>
                  <span className="text-gray-400 ml-1">({storeIds.length} {storeIds.length === 1 ? 'store' : 'stores'})</span>
                </button>
              </div>
              {isOpen && (
                <div className="pl-10 space-y-0.5 pb-1">
                  {storeIds.map(sid => (
                    <div key={sid} className="text-xs text-gray-500">• {storeMap[sid] || sid}</div>
                  ))}
                  {storeIds.length === 0 && <div className="text-xs text-gray-400 italic">No stores in this group</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Targeted summary when groups are selected */}
      {hasTargets && targetedSummary && (
        <div className="mt-3 pt-3 border-t border-gray-200" data-testid="targeted-summary">
          <div className="text-sm font-medium text-blue-700 mb-2">
            Targeted: {targetedSummary.storeCount} {targetedSummary.storeCount === 1 ? 'store' : 'stores'} across {targetedSummary.selected.length} {targetedSummary.selected.length === 1 ? 'group' : 'groups'}
          </div>
          <div className="space-y-1">
            {targetedSummary.selected.map(group => {
              const isOpen = targetExpanded[group.id];
              const storeIds = group.store_ids || [];
              return (
                <div key={group.id}>
                  <button
                    type="button"
                    onClick={() => toggleTarget(group.id)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800"
                    data-testid={`targeted-group-toggle-${group.id}`}
                  >
                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    <span className="font-medium">{group.name}</span>
                    <span className="text-gray-400">({storeIds.length})</span>
                  </button>
                  {isOpen && (
                    <div className="pl-6 space-y-0.5 pb-1">
                      {storeIds.map(sid => (
                        <div key={sid} className="text-xs text-gray-500">• {storeMap[sid] || sid}</div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No targets message */}
      {!hasTargets && (
        <div className="mt-2 text-xs text-amber-600" data-testid="no-target-message">
          No groups targeted — all stores will be shown to learners.
        </div>
      )}
    </div>
  );
}

export default function CourseEditor() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const { canWrite, canPublish } = usePermissions();

  const [course, setCourse] = useState({
    title: '',
    description: '',
    client_id: '',
    role_tags: [],
    target_groups: [],
    experience_type: 'training',
    blocks: []
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchClients();
    if (!isNew) {
      fetchCourse();
    }
  }, [id]);

  // When course.client_id changes, find and set selected client
  useEffect(() => {
    if (course.client_id && clients.length > 0) {
      const client = clients.find(c => c.id === course.client_id);
      setSelectedClient(client || null);
    }
  }, [course.client_id, clients]);

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      const list = response.data.clients || [];
      setClients(list);
      // Phase 4.5 — auto-preselect when the caller has exactly one assignable village
      if (isNew && list.length === 1) {
        setCourse((prev) => (prev.client_id ? prev : { ...prev, client_id: list[0].id }));
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${id}`);
      console.log('Fetched course with blocks:', response.data.blocks?.length || 0);
      console.log('Fetched course description:', response.data.description ? response.data.description.substring(0, 100) + '...' : 'NO DESCRIPTION');
      setCourse(response.data);
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Failed to load course');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!course.title.trim()) {
      toast.error(t('editor_title_label'));
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        console.log('💾 Creating new course with blocks:', course.blocks.length, 'blocks');
        const response = await api.post('/courses', {
          title: course.title,
          description: course.description,
          client_id: course.client_id,
          role_tags: course.role_tags,
          target_groups: course.target_groups,
          experience_type: course.experience_type,
          blocks: course.blocks
        });
        toast.success(`Course created! (${course.blocks.length} blocks)`);
        navigate(`/courses/${response.data.id}/edit`);
      } else {
        // Ensure we have the latest blocks from state
        const blocksToSave = [...course.blocks];
        console.log('💾 Saving course with blocks:', blocksToSave.length, 'blocks');
        console.log('💾 Block IDs:', blocksToSave.map(b => b.id));
        
        const response = await api.put(`/courses/${id}`, {
          title: course.title,
          description: course.description,
          client_id: course.client_id,
          role_tags: course.role_tags,
          target_groups: course.target_groups,
          experience_type: course.experience_type,
          blocks: blocksToSave
        });
        
        console.log('✅ Save response blocks:', response.data.blocks?.length);
        console.log('✅ Response block IDs:', response.data.blocks?.map(b => b.id));
        
        // Update local state with saved data to ensure sync
        setCourse(response.data);
        
        toast.success(`Course saved! (${blocksToSave.length} blocks)`);
      }
    } catch (error) {
      console.error('❌ Failed to save course:', error);
      console.error('❌ Error details:', error.response?.data);
      toast.error(`Failed to save course: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (course.blocks.length === 0) {
      toast.error(t('editor_add_block_before_publish'));
      return;
    }

    try {
      await handleSave();
      await api.post(`/courses/${id}/publish`);
      toast.success('Course published!');
      navigate(`/courses/${id}/publish`);
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish course');
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      props: getDefaultProps(type),
      order: course.blocks.length
    };
    setCourse({ ...course, blocks: [...course.blocks, newBlock] });
  };

  const getDefaultProps = (type) => {
    switch (type) {
      case 'text':
        return { content: 'New text block', align: 'left' };
      case 'image':
        return { url: '', alt: '', caption: '' };
      case 'video':
        return { aspect: '9:16', mp4_url: '', poster: '', duration_s: 0 };
      case 'audio':
        return { url: '', duration_s: 0, title: '', description: '' };
      case 'quiz':
        return { question: 'New question?', choices: [{ id: '1', text: 'Option 1', correct: false }], explanation: '' };
      case 'cta':
        return { text: 'Call to action text', button_text: 'Click here', button_url: '', style: 'primary' };
      default:
        return {};
    }
  };

  const updateBlock = (blockId, newProps) => {
    setCourse({
      ...course,
      blocks: course.blocks.map(b => b.id === blockId ? { ...b, props: newProps } : b)
    });
  };

  const deleteBlock = (blockId) => {
    setCourse({
      ...course,
      blocks: course.blocks.filter(b => b.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    const index = course.blocks.findIndex(b => b.id === blockId);
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === course.blocks.length - 1)) return;

    const newBlocks = [...course.blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    setCourse({ ...course, blocks: newBlocks });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="course-editor">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-xl font-bold">{isNew ? t('editor_title_new') : t('editor_title_edit')}</h1>
          </div>
          <div className="flex gap-2">
            {canWrite && (
              <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-button">
                <Save className="h-4 w-4 mr-2" />
                {saving ? t('editor_saving') : t('editor_save')}
              </Button>
            )}
            {!isNew && course.status === 'draft' && canPublish && (
              <Button onClick={handlePublish} data-testid="publish-button">
                <Eye className="h-4 w-4 mr-2" />
                {t('editor_publish')}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor Panel */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('editor_course_details')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('editor_title_label')}</label>
                  <Input
                    data-testid="course-title-input"
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    placeholder={t('editor_title_placeholder')}
                  />
                </div>

                {/* Experience Type */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    <ClipboardList className="h-4 w-4 inline mr-1" />
                    Experience type
                  </label>
                  <div className="flex gap-3">
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                        course.experience_type !== 'mystery_shop'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="experience-type-training"
                    >
                      <input
                        type="radio"
                        name="experience_type"
                        value="training"
                        checked={course.experience_type !== 'mystery_shop'}
                        onChange={() => setCourse({ ...course, experience_type: 'training' })}
                        className="sr-only"
                      />
                      Training
                    </label>
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                        course.experience_type === 'mystery_shop'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="experience-type-mystery-shop"
                    >
                      <input
                        type="radio"
                        name="experience_type"
                        value="mystery_shop"
                        checked={course.experience_type === 'mystery_shop'}
                        onChange={() => setCourse({ ...course, experience_type: 'mystery_shop' })}
                        className="sr-only"
                      />
                      Mystery Shop
                    </label>
                    <label
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm ${
                        course.experience_type === 'guide'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="experience-type-guide"
                    >
                      <input
                        type="radio"
                        name="experience_type"
                        value="guide"
                        checked={course.experience_type === 'guide'}
                        onChange={() => setCourse({ ...course, experience_type: 'guide' })}
                        className="sr-only"
                      />
                      Handleiding
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('editor_description_label')}</label>
                  <div className="border rounded-md">
                    <RichTextEditor
                      key={`desc-${id || 'new'}`}
                      value={course.description || ''}
                      onChange={(html) => setCourse({ ...course, description: html })}
                      placeholder={t('editor_description_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">{t('course_client_label')}</label>
                  <Select
                    value={course.client_id || ''}
                    onValueChange={(value) => {
                      setCourse({ ...course, client_id: value, target_groups: [] });
                      const client = clients.find(c => c.id === value);
                      setSelectedClient(client || null);
                    }}
                  >
                    <SelectTrigger data-testid="client-select">
                      <SelectValue placeholder={t('course_client_select_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Store Groups with targeting (shown first) */}
                {selectedClient?.store_groups?.length > 0 && (
                  <StoreGroupsDisplay
                    groups={selectedClient.store_groups}
                    stores={selectedClient.stores || []}
                    targetGroups={course.target_groups || []}
                    onTargetGroupsChange={(groups) => setCourse({ ...course, target_groups: groups })}
                  />
                )}
                {/* All village stores — collapsible secondary section when groups exist */}
                {selectedClient && selectedClient.stores?.length > 0 && (
                  selectedClient.store_groups?.length > 0 ? (
                    <details className="mt-3" data-testid="all-stores-section">
                      <summary className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none list-none">
                        <ChevronRight className="h-3 w-3" />
                        <Store className="h-3 w-3" />
                        Show all village stores ({selectedClient.stores.length})
                      </summary>
                      <div className="bg-gray-50 rounded-lg p-3 mt-1 space-y-0.5 max-h-60 overflow-y-auto">
                        {selectedClient.stores.map((store) => (
                          <div key={store.id} className="text-xs text-gray-500 pl-4">• {store.name}</div>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <Store className="h-4 w-4" />
                        {t('course_client_stores_title')}
                      </div>
                      <div className="space-y-1">
                        {selectedClient.stores.map((store) => (
                          <div key={store.id} className="text-sm text-gray-600 pl-6">• {store.name}</div>
                        ))}
                      </div>
                    </div>
                  )
                )}
                {selectedClient && (!selectedClient.stores || selectedClient.stores.length === 0) && (
                  <div className="text-sm text-gray-500 italic">
                    {t('course_client_no_stores')}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Blocks */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">{t('editor_blocks_title')} ({(course.blocks ?? []).length})</h2>
              </div>
              
              {(course.blocks ?? []).length === 0 ? (
                <Card data-testid="no-blocks-message">
                  <CardContent className="py-8 text-center text-gray-500">
                    {t('editor_no_blocks')}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {(course.blocks ?? []).map((block, index) => (
                    <BlockEditor
                      key={block.id}
                      block={block}
                      index={index}
                      onUpdate={(props) => updateBlock(block.id, props)}
                      onDelete={() => deleteBlock(block.id)}
                      onMove={moveBlock}
                      canMoveUp={index > 0}
                      canMoveDown={index < (course.blocks ?? []).length - 1}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add Block Panel */}
          {canWrite && (
            <div className="space-y-4">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle>{t('editor_add_block')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button
                    data-testid="add-text-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('text')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_text')}
                  </Button>
                  <Button
                    data-testid="add-image-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('image')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_image')}
                  </Button>
                  <Button
                    data-testid="add-video-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('video')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_video')}
                  </Button>
                  <Button
                    data-testid="add-audio-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('audio')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_audio')}
                  </Button>
                  <Button
                    data-testid="add-quiz-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('quiz')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_quiz')}
                  </Button>
                  <Button
                    data-testid="add-cta-block"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => addBlock('cta')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('editor_block_cta')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
